import React, { useMemo, useState, useCallback } from "react";

import { useMediaQuery, useTheme } from "@mui/material";

import Box from "@mui/material/Box";
import MoreIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";

const MenuSection = ({ children, divider }) => {
    return (
        <MenuItem
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            sx={{ "&:hover": { background: "none" } }}
            disableRipple
            divider={divider}>
            {children}
        </MenuItem>
    );
};

const MenuTitle = ({ title }) => {
    return <MenuSection>{title}</MenuSection>;
};

const CompositeAction = ({ action, isSmall, disabled }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClose = () => setAnchorEl(null);

    const handleOpen = useCallback(
        (e) => {
            setAnchorEl(e.currentTarget);
            action && action.onClick && action.onClick();
        },
        [action, setAnchorEl]
    );

    return (
        <>
            <Action action={action} onClick={handleOpen} disabled={disabled} />
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl && !disabled}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: "visible",
                        minWidth: 100,
                        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                        mt: 1.5,
                        "&:before": {
                            content: '""',
                            display: "block",
                            position: "absolute",
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: "background.paper",
                            transform: "translateY(-50%) rotate(45deg)",
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                {action.items.map((item, idx) => {
                    if (item.render) {
                        return (
                            <MenuSection key={idx}>
                                {item.render({ onClick: item.onClick, isSmall, disabled })}
                            </MenuSection>
                        );
                    }

                    return (
                        <MenuItem
                            key={idx}
                            onClick={item.onClick}
                            divider={item.divider}
                            disabled={disabled}>
                            <ListItemIcon>
                                {!!item.badgeContent ? (
                                    <Badge
                                        badgeContent={item.badgeContent}
                                        color="error"
                                        variant={item.badgeVariant || undefined}>
                                        {item.icon}
                                    </Badge>
                                ) : (
                                    item.icon
                                )}
                            </ListItemIcon>
                            {item.title}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

const Action = ({ action, onClick, isSmall, disabled }) => {
    if (typeof action.render === "function") {
        return action.render({ onClick, isSmall, disabled });
    }
    return (
        <IconButton onClick={onClick} title={action.title} disabled={disabled}>
            {action.icon}
        </IconButton>
    );
};

const ActionsNew = ({ actions, disabled }) => {
    const theme = useTheme();
    const isSmall = !useMediaQuery(theme.breakpoints.up("md"));

    const items = useMemo(() => {
        if (!Array.isArray(actions) || !actions.length) {
            return [];
        }

        if (!isSmall) {
            return actions.map((a) => ({
                ...a,
                hasItems: Array.isArray(a.items) && !!a.items.length,
            }));
        }

        const items = [];
        const combinedItem = {
            icon: <MoreIcon />,
            items: [],
        };

        for (let a of actions) {
            if (a.alwaysVisible) {
                items.push({ ...a, hasItems: Array.isArray(a.items) && !!a.items.length });
                continue;
            }

            if (Array.isArray(a.items)) {
                //TODO add title? maybe later
                for (let item of a.items) {
                    combinedItem.items.push({ ...item });
                }
            } else {
                combinedItem.items.push({ ...a });
            }
        }

        if (combinedItem.items.length) {
            combinedItem.hasItems = true;
            items.push(combinedItem);
        }
        return items;
    }, [actions, isSmall]);

    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            {items.map((action, idx) => {
                if (action.hasItems) {
                    return (
                        <CompositeAction
                            key={idx}
                            action={action}
                            isSmall={isSmall}
                            disabled={disabled}
                        />
                    );
                }

                return (
                    <Action
                        key={idx}
                        action={action}
                        onClick={action.onClick}
                        disabled={disabled}
                    />
                );
            })}
        </Box>
    );
};

export default ActionsNew;
